import { memo, useCallback } from 'react';
import { WarningAmberOutlined } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { addDays, isAfter, isBefore, isEqual } from 'date-fns';
import {
  EstateTaskInstance_GetAllEstateTaskInstancesWithDeadlineByPortalIdQuery,
  EstateTaskInstanceStatus,
} from 'queries';

import { useStyles } from './deadline-tracker-task-label.styles';

type DeadlineTrackerTaskLabelProps = {
  estateTaskInstance: EstateTaskInstance_GetAllEstateTaskInstancesWithDeadlineByPortalIdQuery['data'][0];
};

export const DeadlineTrackerTaskLabel = memo(
  ({ estateTaskInstance }: DeadlineTrackerTaskLabelProps) => {
    const classes = useStyles();

    const getIsDeadlineWithinDays = useCallback(
      (deadline: Date | string, timeFrameInDays: number) => {
        const deadlineDate = new Date(deadline);
        const now = new Date();
        const sevenDaysFromNow = addDays(now, timeFrameInDays);

        return (
          (isAfter(deadlineDate, now) || isEqual(deadlineDate, now)) &&
          (isBefore(deadlineDate, sevenDaysFromNow) || isEqual(deadlineDate, sevenDaysFromNow))
        );
      },
      [],
    );

    const isDeadlineWithin7Days = getIsDeadlineWithinDays(estateTaskInstance.deadlineDate, 7);

    return (
      <Box>
        <Typography
          key={0}
          className={
            estateTaskInstance.status === EstateTaskInstanceStatus.Completed
              ? classes.estateTaskRowLabelCompleted
              : classes.estateTaskRowLabelNotCompleted
          }
          variant="body1"
          dangerouslySetInnerHTML={{
            __html: estateTaskInstance.labelContext
              ? `<Typography>${estateTaskInstance.label} <strong>(${estateTaskInstance.labelContext})</strong></Typography>`
              : `<Typography>${estateTaskInstance.label}</Typography>`,
          }}
        />
        <Typography
          key={1}
          className={
            estateTaskInstance.labelHint === '' || !estateTaskInstance.labelHint
              ? classes.estateTaskRowNoLabelHint
              : classes.estateTaskRowLabelHint
          }
          variant="body2"
          dangerouslySetInnerHTML={{
            __html: `<Typography>${estateTaskInstance.labelHint}</Typography>`,
          }}
        />
        {isDeadlineWithin7Days && (
          <Typography className={classes.warningText}>
            <WarningAmberOutlined fontSize="small" /> REMINDER: The deadline for this is
            approaching.
          </Typography>
        )}
      </Box>
    );
  },
);
